import * as React from 'react'
import { ImageOff, Loader2, Upload } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Field } from '@/components/ui/field'
import { Input } from '@/components/ui/input'
import { FormError } from '@/components/ui/form-error'
import { requestErrorMessage } from '@/helpers/apiClient'
import { useMarketingImages, useUploadMarketingImage } from '@/hooks/useVenueMarketing'
import { getString, pathKey, setAt, type Path } from './draft'
import type { DraftControl } from './fields'

/** Read a chosen file as the data URL the API expects. */
function readAsDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onerror = () => {
      reject(new Error('That file could not be read.'))
    }
    reader.onload = () => {
      const result = reader.result
      if (typeof result === 'string') resolve(result)
      else reject(new Error('That file could not be read as an image.'))
    }
    reader.readAsDataURL(file)
  })
}

/**
 * An image on a marketing page: what is there, a way to replace it, and the
 * venue's other images to choose from.
 *
 * **The picker is the point.** The same photograph belongs in more than one
 * section often enough that without it a venue uploads its hero shot four times
 * and ends up with four copies it cannot tell apart. Uploading is how an image
 * gets into the list; choosing is how it gets used again.
 *
 * The address is still editable by hand, because ten venues' worth of content
 * arrived as Cloudinary URLs and those keep working — an image field holds an
 * address, and where it points is not this form's business.
 */
export function ImageField({
  control,
  path,
  label,
  hint,
  optional = false,
}: {
  control: DraftControl
  path: Path
  label: string
  hint?: string
  optional?: boolean
}): React.ReactElement {
  const id = React.useId()
  const venueId = control.venueId
  const [picking, setPicking] = React.useState(false)
  const [failure, setFailure] = React.useState<string | null>(null)
  const fileInput = React.useRef<HTMLInputElement>(null)

  const images = useMarketingImages(venueId, picking)
  const upload = useUploadMarketingImage(venueId)

  const value = getString(control.draft, path)
  const error = control.problems.byPath.get(pathKey(path))?.join(' ')

  function put(url: string | undefined): void {
    control.onChange(setAt(control.draft, path, url))
  }

  async function chooseFile(file: File): Promise<void> {
    setFailure(null)
    try {
      const image = await upload.mutateAsync(await readAsDataUrl(file))
      put(image.url)
    } catch (cause) {
      setFailure(requestErrorMessage(cause))
    }
  }

  return (
    <div className="space-y-2">
      <Field
        label={label}
        htmlFor={id}
        {...(hint === undefined ? {} : { hint })}
        {...(error === undefined ? {} : { error })}
      >
        <Input
          id={id}
          value={value}
          placeholder="https://…"
          onChange={(event) => {
            const next = event.target.value
            put(next === '' && optional ? undefined : next)
          }}
        />
      </Field>

      <div className="flex items-start gap-3">
        <Preview url={value} />
        <div className="flex flex-col gap-1">
          <input
            ref={fileInput}
            type="file"
            accept="image/png,image/jpeg,image/webp,image/gif,image/avif"
            className="sr-only"
            onChange={(event) => {
              const file = event.target.files?.[0]
              if (file) void chooseFile(file)
              event.target.value = ''
            }}
          />
          <div className="flex items-center gap-1">
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={upload.isPending}
              onClick={() => {
                fileInput.current?.click()
              }}
            >
              {upload.isPending ? (
                <Loader2 className="mr-1 size-3.5 animate-spin" aria-hidden />
              ) : (
                <Upload className="mr-1 size-3.5" aria-hidden />
              )}
              Upload
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => {
                setPicking(!picking)
              }}
            >
              {picking ? 'Hide' : 'Choose existing'}
            </Button>
            {value !== '' ? (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => {
                  put(optional ? undefined : '')
                }}
              >
                Clear
              </Button>
            ) : null}
          </div>
          {failure !== null ? <FormError>{failure}</FormError> : null}
        </div>
      </div>

      {picking ? (
        <Picker
          loading={images.isLoading}
          error={images.error === null ? null : requestErrorMessage(images.error)}
          urls={(images.data ?? []).map((image) => image.url)}
          chosen={value}
          onChoose={(url) => {
            put(url)
            setPicking(false)
          }}
        />
      ) : null}
    </div>
  )
}

/** What is actually at the address, or why nothing is. */
function Preview({ url }: { url: string }): React.ReactElement {
  const [broken, setBroken] = React.useState(false)
  React.useEffect(() => {
    setBroken(false)
  }, [url])

  if (url === '') {
    return (
      <div className="flex size-20 shrink-0 items-center justify-center rounded-md border border-dashed border-line text-ink-muted">
        <ImageOff className="size-4" aria-hidden />
      </div>
    )
  }
  if (broken) {
    return (
      <div className="flex size-20 shrink-0 items-center justify-center rounded-md border border-danger-ink/50 bg-danger-soft p-1 text-center text-tiny text-danger-ink">
        Will not load
      </div>
    )
  }
  return (
    <img
      src={url}
      alt=""
      className="size-20 shrink-0 rounded-md border border-line object-cover"
      onError={() => {
        setBroken(true)
      }}
    />
  )
}

function Picker({
  loading,
  error,
  urls,
  chosen,
  onChoose,
}: {
  loading: boolean
  error: string | null
  urls: readonly string[]
  chosen: string
  onChoose: (url: string) => void
}): React.ReactElement {
  if (loading) return <p className="text-sm text-ink-muted">Loading…</p>
  if (error !== null) return <FormError>{error}</FormError>
  if (urls.length === 0) {
    return (
      <p className="text-sm text-ink-muted">
        Nothing uploaded for this venue yet. The first upload starts the list.
      </p>
    )
  }
  return (
    <div className="flex flex-wrap gap-2 rounded-md border border-line p-2">
      {urls.map((url) => (
        <button
          key={url}
          type="button"
          aria-label="Use this image"
          aria-pressed={url === chosen}
          className={`rounded-md border p-0.5 ${url === chosen ? 'border-primary' : 'border-transparent hover:border-line'}`}
          onClick={() => {
            onChoose(url)
          }}
        >
          <img src={url} alt="" className="size-16 rounded object-cover" />
        </button>
      ))}
    </div>
  )
}
